import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const EmailInput = z.object({ email: z.string().trim().toLowerCase().email() });

export type LookupEmailResult = {
  userExists: boolean;
  knownContact: { nombre: string | null; apellidos: string | null } | null;
};

export const lookupEmail = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => EmailInput.parse(data))
  .handler(async ({ data }): Promise<LookupEmailResult> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // 1) Fuente de verdad: ¿existe una cuenta en auth.users con este email?
    const { data: authExists, error: authError } = await supabaseAdmin.rpc("auth_email_exists", {
      _email: data.email,
    });
    if (authError) throw new Error("No se ha podido comprobar el correo. Inténtalo de nuevo.");
    if (authExists === true) {
      return { userExists: true, knownContact: null };
    }

    // 2) Puede haber varios contactos con el mismo email (duplicados):
    //    no usar maybeSingle(), que falla con >1 fila y devolvía "no existe".
    const { data: contactos, error: contactError } = await supabaseAdmin
      .from("contactos")
      .select("nombre, apellidos, user_id")
      .ilike("email", data.email)
      .order("user_id", { ascending: false, nullsFirst: false })
      .limit(5);
    if (contactError) throw new Error("No se ha podido comprobar el correo. Inténtalo de nuevo.");

    const list = contactos ?? [];
    if (list.some((c) => c.user_id)) {
      return { userExists: true, knownContact: null };
    }
    const known = list.find((c) => c.nombre || c.apellidos) ?? list[0];
    if (known) {
      return {
        userExists: false,
        knownContact: { nombre: known.nombre, apellidos: known.apellidos },
      };
    }
    return { userExists: false, knownContact: null };
  });

const PhoneInput = z.object({ telefono: z.string().trim().min(6).max(40) });

export const linkContactoByPhone = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => PhoneInput.parse(data))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Solo vincula contactos sin user_id (libres) que coincidan exactamente por teléfono.
    const { error } = await supabaseAdmin
      .from("contactos")
      .update({ user_id: context.userId })
      .eq("telefono", data.telefono)
      .is("user_id", null);

    if (error) throw error;
    return { ok: true };
  });
